import { PORTRAYAL_PACKS } from "./portrayal";

function parseXml(xmlText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(xmlText || ""), "application/xml");
  const err = doc.querySelector("parsererror");
  if (err) throw new Error("Invalid XML");
  return doc;
}

function textOf(el) {
  return String(el?.textContent || "").trim();
}

function collectNames(doc, selectors) {
  const out = new Set();
  for (const sel of selectors) {
    doc.querySelectorAll(sel).forEach((n) => {
      const t = textOf(n);
      if (t) out.add(t);
    });
  }
  return [...out];
}

function parseMaybeNumber(v) {
  const n = Number(String(v || "").trim());
  return Number.isFinite(n) ? n : null;
}

function extractObjectClassHints(typeName = "") {
  const t = String(typeName || "").toLowerCase();
  const out = new Set();
  if (!t) return out;
  if (t.includes("sounding") || t.includes("soundg")) out.add("SOUNDG");
  if (t.includes("depth") || t.includes("depare")) out.add("DEPARE");
  if (t.includes("light") || t.includes("lights")) out.add("LIGHTS");
  if (t.includes("buoy")) out.add("BOY");
  if (t.includes("beacon") || t.includes("daymark") || t.includes("topmark")) out.add("BCN");
  if (t.includes("wreck")) out.add("WRECKS");
  if (t.includes("obstruction") || t.includes("rock")) out.add("OBSTRN");
  if (out.size === 0 && t.includes("point")) out.add("PNTGEN");
  if (out.size === 0 && t.includes("line")) out.add("LINGEN");
  if (out.size === 0 && (t.includes("area") || t.includes("polygon"))) out.add("AREGEN");
  return out;
}

function parseInstructionEntries(doc) {
  const entries = [];
  const nodes = doc.querySelectorAll("featurePortrayal, FeaturePortrayal, portrayalInstruction, PortrayalInstruction");
  nodes.forEach((node) => {
    const featureType = textOf(
      node.querySelector("featureType")
      || node.querySelector("featureCode")
      || node.querySelector("featureName")
      || node.querySelector("code"),
    );
    const priority = parseMaybeNumber(
      textOf(
        node.querySelector("displayPriority")
        || node.querySelector("drawingPriority")
        || node.querySelector("priority"),
      ),
    );
    const viewGroup = textOf(
      node.querySelector("viewingGroup")
      || node.querySelector("viewGroup")
      || node.querySelector("displayPlane"),
    );
    const objectClasses = [...extractObjectClassHints(featureType)];
    entries.push({
      featureType,
      objectClasses,
      priority,
      viewGroup: viewGroup || null,
    });
  });
  return entries;
}

export function parsePortrayalManifest(xmlText) {
  const doc = parseXml(xmlText);
  const root = doc.documentElement;
  const title = textOf(
    doc.querySelector("name")
    || doc.querySelector("title")
    || doc.querySelector("catalogName")
    || root,
  );
  const featureTypes = collectNames(doc, [
    "featureType name",
    "FeatureType name",
    "featureType code",
    "FeatureType code",
  ]);
  const instructionCount = doc.querySelectorAll("portrayalInstruction, PortrayalInstruction, featurePortrayal, FeaturePortrayal").length;
  const entries = parseInstructionEntries(doc);
  const objectClassSet = new Set();
  const classOverrides = {};
  for (const e of entries) {
    for (const oc of e.objectClasses || []) {
      objectClassSet.add(oc);
      const existing = classOverrides[oc] || {};
      classOverrides[oc] = {
        priority: Number.isFinite(Number(e.priority)) ? Number(e.priority) : (existing.priority ?? null),
        viewGroup: e.viewGroup || existing.viewGroup || null,
      };
    }
  }
  return {
    title: title || "Unnamed Portrayal XML",
    featureTypes,
    instructionCount,
    objectClasses: [...objectClassSet],
    classOverrides,
  };
}

function cloneRules(rules) {
  return Array.isArray(rules) ? [...rules] : [];
}

function normalizeViewGroup(v) {
  const s = String(v || "").toLowerCase();
  if (!s) return null;
  if (s.includes("label")) return "labels";
  if (s.includes("point")) return "points";
  if (s.includes("line")) return "lines";
  if (s.includes("area") || s.includes("surface")) return "areas";
  return null;
}

export function buildPackFromManifest(manifest, options = {}) {
  const baseId = String(options.basePackId || "enc-v0.1");
  const base = PORTRAYAL_PACKS[baseId] || PORTRAYAL_PACKS["enc-v0.1"];
  const ts = Date.now();
  const id = `enc-xml-${ts}`;
  const label = `${manifest?.title || "Portrayal XML"} (${id})`;
  const allowedClasses = new Set(Array.isArray(manifest?.objectClasses) ? manifest.objectClasses : []);
  const classOverrides = manifest?.classOverrides && typeof manifest.classOverrides === "object" ? manifest.classOverrides : {};
  const transformed = cloneRules(base?.rules).map((rule) => {
    const objectClass = String(rule?.objectClass || "").trim();
    if (!objectClass || objectClass === "GENERIC") return { ...rule };
    const keepActive = allowedClasses.size === 0 || allowedClasses.has(objectClass) || objectClass === "PNTGEN";
    const originalMatch = typeof rule.match === "function" ? rule.match : (() => true);
    const override = classOverrides[objectClass] || {};
    return {
      ...rule,
      viewGroup: normalizeViewGroup(override.viewGroup) || rule.viewGroup,
      priority: Number.isFinite(Number(override.priority)) ? Number(override.priority) : rule.priority,
      match: (ctx) => keepActive && originalMatch(ctx),
    };
  });
  return {
    id,
    label,
    source: {
      kind: "xml-manifest",
      loadedAt: new Date(ts).toISOString(),
      featureTypes: manifest?.featureTypes || [],
      instructionCount: Number(manifest?.instructionCount || 0),
      objectClasses: manifest?.objectClasses || [],
    },
    rules: transformed,
  };
}

export async function loadPortrayalPackFromUrl(url, options = {}) {
  const u = String(url || "").trim();
  if (!u) throw new Error("URL is required");
  const res = await fetch(u);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const xmlText = await res.text();
  const manifest = parsePortrayalManifest(xmlText);
  const pack = buildPackFromManifest(manifest, options);
  return { pack, manifest };
}
